import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { tenantService } from '@/services/tenant-service';
import type { Tenant } from '@/types/core/tenant';
import { NIL_UUID, type UUID } from '@/utils/uuid';

import { tenantQueryKeys } from './query-keys';

/**
 * Hook to get the current tenant based on the URL slug parameter.
 * Uses React Query to fetch tenant data with cache so we don't call
 * GET /api/v1/tenants/{id} on every navigation/focus.
 */
export function useCurrentTenant() {
  const { tenantSlug, slug } = useParams<{
    tenantSlug?: string;
    slug?: string;
  }>();

  // Support both `tenantSlug` and `slug` params for flexibility
  const currentSlug = tenantSlug ?? slug;

  return useQuery<Tenant, Error>({
    queryKey: tenantQueryKeys.current(currentSlug),
    queryFn: async () => {
      if (!currentSlug) throw new Error('No tenant slug');
      const tenant = await tenantService.findSlug(currentSlug);
      if (!tenant) throw new Error(`Tenant not found: ${currentSlug}`);
      return tenant;
    },
    enabled: !!currentSlug,
    retry: 5,
    staleTime: 1 * 60 * 1000, // 1 minute
    // Override global defaults (`'always'`) so this query only refetches when stale.
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

/**
 * Optional hook to get the current tenant ID.
 * Returns undefined while loading or when no tenant is available.
 */
export function useOptionalCurrentTenantID(): UUID | undefined {
  const { data: tenant, isLoading } = useCurrentTenant();
  if (isLoading) return undefined;
  return tenant?.id;
}

/**
 * Hook to get the current tenant ID.
 * Throws an error if no tenant is available.
 * Use this in components that require a tenant to be present.
 */
export function useCurrentTenantID(): UUID {
  const tenantId = useOptionalCurrentTenantID();
  return tenantId ?? NIL_UUID;
}

/**
 * Optional hook to get current tenant slug from URL params.
 * Returns undefined when no slug is present.
 */
export function useOptionalCurrentTenantSlug(): string | undefined {
  const { tenantSlug, slug } = useParams<{
    tenantSlug?: string;
    slug?: string;
  }>();
  const currentSlug = tenantSlug ?? slug;
  if (!currentSlug) return undefined;
  const trimmed = currentSlug.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Hook to get the current tenant slug from URL params.
 * Throws an error if no tenant slug is available.
 */
export function useCurrentTenantSlug(): string {
  const currentSlug = useOptionalCurrentTenantSlug();
  return currentSlug ?? '';
}
